// One piece of plant (Task U; prototype §23a assetModal): what it is and where, the service cycle and when it is next
// due, the service history with each certificate, its documents, and the work orders raised on it.
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Wrench } from 'lucide-react';
import { api } from '../lib/api';
import { Card, Badge, Button, KeyValue, Money, DateText, PageHeader } from '../components/ui';
import { formatDate } from '../lib/viz';
import { openStoredFile } from '../lib/reservations';
import { AssetDocumentsCard } from '../components/maintenance/AssetDocumentsCard';
import { LogServiceDialog } from '../components/maintenance/LogServiceDialog';
import { AssetForm } from '../components/forms/AssetForm';
import { PageLoader } from '../components/BrandLoader';

type A = Record<string, any>;

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [logging, setLogging] = useState(false);
  const [editing, setEditing] = useState(false);
  const { data: a, isLoading, error } = useQuery({ queryKey: ['asset', id], queryFn: async () => (await api.get<A>(`/assets/${id}`)).data, enabled: !!id });
  if (isLoading) return <PageLoader />;
  if (!a) return <div className="py-12 text-center text-sm text-slate-500">{(error as Error)?.message ?? 'Asset not found'}</div>;
  const dueTone = a.serviceOverdue ? 'critical' : a.nextServiceDue && a.nextServiceDue <= new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10) ? 'warning' : 'good';

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader back={`/app/properties/${a.propertyId}?tab=building&sub=assets`} title={a.name} eyebrow={`${a.qrCode} · ${a.propertyName}${a.unitReference ? ` · ${a.unitReference}` : ' · communal'}${a.make ? ` · ${a.make} ${a.model ?? ''}` : ''}`}
        actions={<><Badge value={a.status} />{a.nextServiceDue && <Badge value={`Next service ${formatDate(a.nextServiceDue)}${a.serviceOverdue ? ' — overdue' : ''}`} tone={dueTone} />}</>}
        menu={[{ label: 'Log a service', icon: 'Wrench', can: 'assets.update', onClick: () => setLogging(true) }, { label: 'Edit asset', icon: 'Pencil', can: 'assets.update', onClick: () => setEditing(true) }]} />

      <div className="grid gap-2.5 lg:grid-cols-3">
        <div className="space-y-2.5 lg:col-span-2">
          <Card title="Details">
            <KeyValue items={[
              { label: 'Category', value: a.category ?? '—' }, { label: 'Serial number', value: a.serialNumber ?? '—' },
              { label: 'Where it is', value: a.location ?? '—' }, { label: 'Serviced by', value: a.servicedByName ?? '—' },
              { label: 'Installed', value: <DateText value={a.installedOn} /> }, { label: 'Warranty', value: a.warrantyExpiresOn ? <>{formatDate(a.warrantyExpiresOn)} {a.underWarranty ? <Badge value="Under warranty" tone="good" /> : <Badge value="Expired" tone="neutral" />}</> : '—' },
              { label: 'Service every', value: a.ppmIntervalDays ? `${a.ppmIntervalDays} days` : '—' }, { label: 'Last serviced', value: <DateText value={a.lastServicedOn} /> },
              { label: 'Next service', value: <DateText value={a.nextServiceDue} /> }, { label: 'Expected life', value: a.expectedLifeYears ? `${a.expectedLifeYears} years` : '—' },
              { label: 'Purchase cost', value: <Money minor={a.purchaseCostMinor} currency={a.currency} /> }, { label: 'Servicing a year', value: <Money minor={a.serviceCostPaMinor} currency={a.currency} /> },
              { label: 'Open work orders', value: String(a.openWorkOrders ?? 0) },
            ]} />
          </Card>
          <AssetDocumentsCard assetId={a.id} />
          <Card title="Service history" subtitle={a.services?.length ? `${a.services.length} logged` : undefined}>
            {!a.services?.length ? <p className="text-[13px] text-ink-400">No services logged here yet. Logging one rolls the next date forward and files the certificate.</p> : (
              <ul className="divide-y divide-ink-100">{a.services.map((s: A) => (
                <li key={s.id} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[13px] font-medium text-ink-800"><Wrench className="h-3.5 w-3.5 text-ink-400" /> {formatDate(s.servicedOn)}{s.vendorName ? ` · ${s.vendorName}` : ''}</div>
                    <div className="text-[12px] text-ink-500">{[s.note, s.nextServiceDue ? `next due ${formatDate(s.nextServiceDue)}` : null, s.performedByName ? `logged by ${s.performedByName}` : null].filter(Boolean).join(' · ')}</div>
                  </div>
                  {s.certificate ? <Button size="sm" variant="secondary" onClick={() => void openStoredFile(s.certificate.url)}><FileText className="h-3.5 w-3.5" /> Certificate</Button> : <span className="text-[11.5px] text-amber-700">No certificate</span>}
                </li>))}</ul>
            )}
          </Card>
        </div>
        <div className="space-y-2.5">
          <Card title="Work orders on it">
            {!a.history?.length ? <p className="text-[13px] text-ink-400">None.</p> : (
              <ul className="divide-y divide-ink-100">{a.history.map((w: A) => (
                <li key={w.id} className="py-2">
                  <Link to={`/app/work-orders/${w.id}`} className="block text-[13px] font-medium text-ink-800 hover:underline">{w.title}</Link>
                  <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-ink-500"><span className="font-mono">{w.reference}</span><Badge value={w.status} />{w.vendorName && <span>{w.vendorName}</span>}<span>{formatDate(w.reportedAt)}</span></div>
                </li>))}</ul>
            )}
          </Card>
        </div>
      </div>
      {logging && <LogServiceDialog asset={a} open onClose={() => setLogging(false)} />}
      {editing && <AssetForm open asset={a} onClose={() => setEditing(false)} />}
    </div>
  );
}
